/*
Copyright 2022 Alexandra Instituttet

Der er givet en begrænset licens af denne software til GEO. Licensen
giver dem ret til at benytte, videreudvikle og fejlrette udelukkende
med henblik på brug i GeoAtlasStudy web-applikationen.
*/
/* eslint-disable no-unused-vars */

import {vec3}          from 'gl-matrix';
import {W3DTile}       from './W3DTile.js';
import {intersectsBox} from 'frustum-intersects';
import {WBox}          from 'wfundament';

class W3DTileSet {
  constructor() {
    this.tiles_ = [];
    this.metaData_ = null;
    this.gisInfo_ = null;
    this.rootTransform_ = null;
  }

  extractUrlBase(url) {
    const index = url.lastIndexOf("/");
    if (index === -1) {
      return "./";
    }
    return url.substr(0, index + 1);
  }

  load3DTileSet(json, url) {
    this.resourcePath_ = this.extractUrlBase(url);
    const rootTile = new W3DTile(json, this.resourcePath_, "ADD", -1);
    rootTile.setGeometricError(json.geometricError);
    this.rootTransform = json.transform;
    this.tiles_.push(rootTile);

    const childIndices = [];

    if (json.children) {
      for (let i = 0; i < json.children.length; i++) {
        var tileNode = new W3DTile(
          json.children[i],
          this.resourcePath_,
          rootTile.getRefineStrategy(),
          0
        );
        const tileNodeIndex = this.tiles_.length;
        this.tiles_.push(tileNode);
        this.addChildIndexToTile(0, tileNodeIndex);
        childIndices.push(tileNodeIndex);
      }
    }

    // defer load on this level to have the root children close at hand for
    // inspection.
    for (let i = 0; i < childIndices.length; i++) {
      this.loadChildTiles(tileNode.json_, childIndices[i]);
    }
  }

  loadChildTiles(json, tileNodeIndex) {
    const parentTile = this.getTile(tileNodeIndex);

    if (json.children) {
      for (let i = 0; i < json.children.length; i++) {
        const tileNode = new W3DTile(
          json.children[i],
          this.resourcePath_,
          parentTile.getRefineStrategy(),
          tileNodeIndex
        );
        const tileIndex = this.tiles_.length;
        this.tiles_.push(tileNode);
        this.addChildIndexToTile(tileNodeIndex, tileIndex);

        if (tileNode.json_.children) {
          this.loadChildTiles(tileNode.json_, tileIndex); // recurse
        }
      }
    }
  }

  filterVisibleLeaves(
    camera /* instance of wOrbitCamera */,
    transform /* mat4 */,
    maxDistance /* number */
  ) {
    const vp = camera.getViewProjection();
    // Transform is an optional mat4 transformation from geographical to
    // frustum coordinates.
    const nodeIntersection = (node, hitRecord) => {
      const b = node.boundingVolume.box;
      const bbMin = vec3.create();
      const bbMax = vec3.create();

      if (transform) {
        vec3.transformMat4(
          bbMin,
          [b[0] - b[3], b[1] - b[7], b[2] - b[11]],
          transform
        );
        vec3.transformMat4(
          bbMax,
          [b[0] + b[3], b[1] + b[7], b[2] + b[11]],
          transform
        );
      } else {
        vec3.set(bbMin, [b[0] - b[3], b[1] - b[7], b[2] - b[11]]);
        vec3.set(bbMax, [b[0] + b[3], b[1] + b[7], b[2] + b[11]]);
      }

      const bbox = new WBox("bb", bbMin, bbMax);
      if (!intersectsBox(vp, [bbMin, bbMax])) {
        return false;
      }
      // if ( frustum.frustumCullBox(bbox) ) {
      // return false;
      // }
      if (!node.children) {
        node.distance_ = vec3.distance(camera.getPosition(), [
          0.5 * (bbMin[0] + bbMax[0]),
          0.5 * (bbMin[1] + bbMax[1]),
          0.5 * (bbMin[2] + bbMax[2]),
        ]);
        if (maxDistance && node.distance_ > maxDistance) {
          return false;
        } else {
          hitRecord.hitNodes.push(node);
          return true;
        }
      }

      let isec = false;
      for (let i = 0; i < node.children.length; i++) {
        isec |= nodeIntersection(node.children[i], hitRecord);
      }

      return isec;
    };

    const hitRecord = { hitNodes: [] };

    nodeIntersection(this.tiles_[0].json_, hitRecord);

    // Sort from camera pos
    hitRecord.hitNodes.sort((a, b) => {
      return a.distance_ - b.distance_;
    });

    return hitRecord;
  }

  addChildIndexToTile(tileIndex, childIndex) {
    this.tiles_[tileIndex].addChildIndex(childIndex);
  }

  getTile(tileIndex) {
    return this.tiles_[tileIndex];
  }
}

export { W3DTileSet };
